import * as z from "zod";

/**
 * Represents a client-side tool call that needs to be executed in the browser
 * rather than on the server. Used when the agent requests a tool marked with
 * isClientSide: true.
 */
export interface ClientSideToolCall
  extends z.infer<typeof ClientSideToolCallSchema_> {}

export const ClientSideToolCallSchema_ = z.object({
  id: z.string(),
  toolName: z.string(),
  args: z.json().meta({ annotation: "JSON" }),
});

export const ClientSideToolCallSchema: z.ZodType<ClientSideToolCall> =
  ClientSideToolCallSchema_;

/**
 * Result from executing a client-side tool, to be submitted back to the agent.
 * Generic version that requires typing the output.
 */
export interface ClientSideToolResult<TOutput> {
  toolCallId: string;
  output: TOutput;
  isError?: boolean;
  errorMetadata?: Record<string, unknown>;
}

export const ClientSideToolResultSchema_ = z.object({
  toolCallId: z.string(),
  output: z.json().meta({ annotation: "JSON" }),
  isError: z.boolean().optional(),
  errorMetadata: z.record(z.string(), z.json()).optional(),
});

export const ClientSideToolResultSchema: z.ZodType<
  ClientSideToolResult<unknown>
> = ClientSideToolResultSchema_;

/**
 * Sentinel prefix on the text part of user messages the server injects to
 * carry a client-tool screenshot into model context as a real image part.
 * Chat UIs filter these messages out of the rendered conversation — the
 * screenshot already appears in the tool's own card.
 */
export const CLIENT_TOOL_IMAGE_MESSAGE_PREFIX = "[client-tool-screenshot]";

export function isClientToolImageMessageText(text: string): boolean {
  return text.startsWith(CLIENT_TOOL_IMAGE_MESSAGE_PREFIX);
}

/**
 * Helper type to define a client-side tool's args and output types
 */
export interface ClientSideToolDefinition<TArgs, TOutput> {
  args: TArgs;
  output: TOutput;
}

/**
 * Generic type for creating a client-side tool map
 * Maps tool names to their argument and output types
 *
 * @example
 * type MyTools = ClientSideToolMap<{
 *   showAlert: ClientSideToolDefinition<{ message: string }, { success: boolean }>;
 *   fetchData: ClientSideToolDefinition<{ url: string }, { data: any[] }>;
 * }>;
 */
export type ClientSideToolMap<
  TTools extends Record<string, ClientSideToolDefinition<any, any>>,
> = TTools;

/**
 * Type-safe executor function for a specific tool
 */
export type ClientSideToolExecutor<TArgs, TOutput> = (
  args: TArgs,
) => Promise<TOutput> | TOutput;

/**
 * Registry of client-side tool executors
 * Maps tool names to their executor functions with proper typing
 */
export type ClientSideToolExecutorRegistry<
  TToolMap extends Record<string, { args: any; output: any }>,
> = {
  [K in keyof TToolMap]: ClientSideToolExecutor<
    TToolMap[K]["args"],
    TToolMap[K]["output"]
  >;
};

/**
 * Generic executor that runs client-side tools with type safety
 * @param toolCall - The tool call to execute
 * @param executors - Registry of executor functions for each tool
 * @param parseArgs - Function to parse and validate args for a given tool name
 * @returns A ClientSideToolResult with the execution output
 */
export async function executeClientSideTool<
  TToolMap extends Record<string, { args: any; output: any }>,
  TToolName extends keyof TToolMap & string,
>({
  toolCall,
  executors,
  parseArgs,
}: {
  toolCall: ClientSideToolCall;
  executors: ClientSideToolExecutorRegistry<TToolMap>;
  parseArgs: <T extends TToolName>(
    toolName: T,
    args: unknown,
  ) => TToolMap[T]["args"];
}): Promise<ClientSideToolResult<unknown>> {
  try {
    const toolName = toolCall.toolName as TToolName;
    const executor = executors[toolName];

    if (!executor) {
      throw new Error(`No executor found for tool: ${toolName}`);
    }

    // Parse and validate the arguments
    const parsedArgs = parseArgs(toolName, toolCall.args);

    // Execute the tool
    const output = await executor(parsedArgs);

    return { toolCallId: toolCall.id, output, isError: false };
  } catch (error) {
    return {
      toolCallId: toolCall.id,
      output: { error: "Tool execution failed" },
      isError: true,
      errorMetadata: {
        message: error instanceof Error ? error.message : String(error),
      },
    };
  }
}

/**
 * Execute multiple client-side tools in parallel
 */
export async function executeClientSideTools<
  TToolMap extends Record<string, { args: any; output: any }>,
>({
  toolCalls,
  executors,
  parseArgs,
}: {
  toolCalls: ClientSideToolCall[];
  executors: ClientSideToolExecutorRegistry<TToolMap>;
  parseArgs: <T extends keyof TToolMap & string>(
    toolName: T,
    args: unknown,
  ) => TToolMap[T]["args"];
}): Promise<ClientSideToolResult<unknown>[]> {
  return Promise.all(
    toolCalls.map((toolCall) =>
      executeClientSideTool({ toolCall, executors, parseArgs }),
    ),
  );
}
